/* eslint-disable max-lines-per-function */
import { useCallback } from 'react';
import BytedUploader, { Config as BytedUploaderConfig } from '@byted/uploader-oversea';
import SliceUploader from '@ad/tos-slice-upload2';
import { checkSuccessResponse } from '@ad/ad_utils/src/utils/common';
import { get2, post2 } from '../../utils/mAxios';
import { isLocal, uploaderTimerFunc, getExtensionFromName } from '../../utils';
import { typeJudge } from '../../utils/mime';
import { generateBoeImageUrl, generateImageUrl } from '../Uploader/useUpload';
import { message } from '@byte-design/ui';
import {
  UploadConfigSchema,
  SuccessDataSchema,
  UploadTypeEnum,
  NewUploaderRegionsMap,
  NewUploaderImageHostMap,
  NewUploaderVideoHostMap
} from './type';
import { getUserLocation } from '../../utils/request';
import Axios from 'axios';
import { getTosFileUrl } from '../../utils/tos-imagex';
import { transformObjToCase } from '@ad/ad_utils/src/utils/universal-common';
import { getVideoInfoByTosUri } from '../../utils/video';
import { FileType } from '../business/TTCX/AssetManagement/common/type';
import { parseZipFile } from '../../utils/file';
import JSZip from 'jszip';
import Cookie from 'js-cookie';

export const TCEX_VIDEO_UPLOAD_NAMESPACE = 'ad_site';
export const TCEX_VIDEO_UPLAOD_NAME = 'tcex_bff';
export const TCEX_APP_ID = 5143;

const defaultUploadUserId = TCEX_VIDEO_UPLAOD_NAME;
const defaultUploadAppId = TCEX_APP_ID;
const defaultUploadSpaceName = TCEX_VIDEO_UPLOAD_NAMESPACE;
// const boeUploadSpaceName = 'openapi-bff-boei18n';
const boeImageXServiceId = 'f3ioyg2qyz';
const defaultImageXServiceIdMap = {
  sg: 'hdprqziq2y',
  va: 'gejt5lnkjp',
  boei18n: boeImageXServiceId
};
// const bffUploadPath = isInternalOrigin ? '/ccs_bff/upload/internal' : '/ccs_bff/upload';
let bffUploadPath = '/ccs_bff/upload';

function getCDNPath(resource: string) {
  if (resource[0] !== '/') {
    // eslint-disable-next-line no-param-reassign
    resource = `/${resource}`;
  }
  return isLocal
    ? `https://tosv.boei18n.remove.this/obj${resource}`
    : `https://sf16-sg.tiktokcdn.com/obj${resource}`;
}

export async function fetchSecretKey(onFail?: (err) => void) {
  const { data, code, msg } = await get2(`${bffUploadPath}/getUploadSecretKey`);
  if (!checkSuccessResponse(code)) {
    onFail?.(code === 10004 ? new Error('NOT_LOGGED_IN') : new Error(msg));
    return {
      code
    };
  }
  return {
    data: data?.key,
    code,
    msg
  };
}

function checkLottieZip(zipResult: JSZip) {
  const searchResult = Object.entries(zipResult.files)
    .filter(([name]) => {
      return !/\..*?\.json/.test(name);
    })
    .find(([name]) => /\.json$/.test(name));
  return searchResult;
}

async function resolveLottie(
  file: File,
  zipResult: JSZip,
  searchResult,
  config: UploadConfigSchema,
  secretKeyLottieJson,
  secretKey,
  fileId?: string
) {
  try {
    const [lottieFilePath, lottieJSONFile] = searchResult;
    const lottieJSON = JSON.parse(await lottieJSONFile.async('string'));
    const { w, h, fr: fps, ip, op, assets } = lottieJSON;
    const duration = (op - ip) / fps;
    if (!(w && h && w <= 720 && h <= 1280 && w / h === 720 / 1280)) {
      config.onFail!(
        {
          name: 'template resolution error',
          message: 'template resolution error'
        },
        fileId
      );
      return;
    }
    if (Math.floor(fps) > 30) {
      config.onFail!(
        {
          name: 'template fps error',
          message: 'template fps error'
        },
        fileId
      );
      return;
    }
    if (duration > 16) {
      config.onFail!(
        {
          name: 'template duration error',
          message: 'template duration error'
        },
        fileId
      );
      return;
    }
    if (!assets?.length) {
      config.onFail!(
        {
          name: 'assets length is zero',
          message: 'assets length is zero'
        },
        fileId
      );
      return;
    }

    const enableMusic = assets?.some((asset) => {
      return /^(aud|audio)/.test(asset.id);
    });
    if (enableMusic) {
      config.onFail!(
        {
          name: 'enable music',
          message: 'enable music'
        },
        fileId
      );
      return;
    }

    // 检测是使用了图片转base64， 如果使用了，会导致json过大
    const enableIncludeInJSON = assets
      .filter((asset) => /^(img|image)/.test(asset.id))
      .some((asset) => !!asset.e);
    if (enableIncludeInJSON) {
      config.onFail!(
        {
          name: 'include in json',
          message: 'include in json'
        },
        fileId
      );
      return;
    }

    //upload original lottie json and lottie zip
    const jsonFile = zipResult.files[lottieFilePath];
    if (jsonFile) {
      const formattedLottieJson = JSON.stringify(JSON.parse(await lottieJSONFile.async('string')), null, 2);
      const formattedLottieJsonFile = new File([formattedLottieJson], jsonFile.name, {
        type: 'application/json'
      });
      const { abortFunc: abortFuncJson, promise: promiseLottieJson } = await multiPurposeUpload(
        formattedLottieJsonFile,
        config,
        secretKeyLottieJson
      );
      const { abortFunc: abortFuncZip, promise: promiseLottieZip } = await multiPurposeUpload(
        file,
        config,
        secretKey
      );
      const abortFuncLottie = () => {
        abortFuncJson();
        abortFuncZip();
      };
      Promise.all([promiseLottieJson, promiseLottieZip])
        .then(async ([resJson, resZip]) => {
          if (config.onSuccess) {
            config.onSuccess(
              resZip as SuccessDataSchema,
              resJson as SuccessDataSchema,
              FileType.Json,
              fileId
            );
          }
        })
        .catch((err: Error) => {
          if (config.onFail) {
            config.onFail(err, fileId);
          }
        });
      return abortFuncLottie;
    }
    config.onFail!(
      {
        name: 'No json found',
        message: 'No json found'
      },
      fileId
    );
  } catch (err) {
    config.onFail!(err as Error, fileId);
  }
}

async function getUploaderInitParams(region?: string) {
  const defaultRegion = region || (isLocal ? 'boei18n' : 'sg');
  const defaultParams = {
    region: defaultRegion,
    spaceName: defaultUploadSpaceName,
    imageServiceId: defaultImageXServiceIdMap[defaultRegion],
    uploadHost: isLocal ? 'https://vas-boei18n.remove.this' : NewUploaderVideoHostMap[defaultRegion]
  };
  const res = await get2<{
    region: string;
    spaceName: string;
    imageServiceId: string;
    uploadHost: string;
  } | null>('/ccs/api/upload/get_uploader_init_params', { uploadRegion: region });
  if (!checkSuccessResponse(res.code)) {
    return defaultParams;
  }
  return {
    region: res.data?.region || defaultParams.region,
    spaceName: res.data?.spaceName || defaultParams.spaceName,
    // Temporarily use hardcoded id, waiting for subsequent iteration
    imageServiceId: defaultParams.imageServiceId,
    uploadHost: defaultParams.uploadHost
  };
}

async function fetchImageUploadAuth(region: string, secretKey?: string) {
  // const res = await get2<{
  // eslint-disable-next-line max-len
  //     sessionToken: string; // 'STS2eyJMVEFjY2Vzc0tleUlEIjoiQUtBUE1qYzBZell4TldaaU5tRmxOREl5T1RrNE9XVmpNek0zTVdNNVpqQTRaRFEiLCJBY2Nlc3NLZXlJRCI6IkFLVFBaakV3TkRobFlUUmpZVGc0TkRnMk1UazFaalZpTjJFMlpEYzFNMk16WWprIiwiU2lnbmVkU2VjcmV0QWNjZXNzS2V5IjoiMHQyRWNsT3R1bmFLTG55TGxhU1VjWkpXTDVWRUUrZWd6eEMyVWZHQjkvK3o1WmY5emdRMGgxWERjWDI2K1YyMWRwZ1pDMUpJak9pOUtHRmZpaFFQcHloc2t3U3lZRHZ2Z2FCVVU0ZHhUc2s9IiwiRXhwaXJlZFRpbWUiOjE2Nzg5Njg4MTgsIlBvbGljeVN0cmluZyI6IntcIlN0YXRlbWVudFwiOlt7XCJFZmZlY3RcIjpcIkFsbG93XCIsXCJBY3Rpb25cIjpbXCJ2b2Q6KlwiLFwiSW1hZ2VYOipcIl0sXCJSZXNvdXJjZVwiOltcIipcIl19LHtcIkVmZmVjdFwiOlwiQWxsb3dcIixcIkFjdGlvblwiOltcIlBTTVwiXSxcIlJlc291cmNlXCI6W1wiYWQudHRjeC5jZmNcIl19XX0iLCJTaWduYXR1cmUiOiI0MGNiNjk2YzY0NWRmMjIwZjgyZjFmZjE1YjU0YzVhZjg4OGE1NDc0OTlkZDQ5MGM0YWQ5NjMzZTU0MjFhMTZjIn0'
  //     accessKeyId: string; // 'AKTPZjEwNDhlYTRjYTg4NDg2MTk1ZjViN2E2ZDc1M2MzYjk'
  //     secretAccessKey: string; // 'MH6Bb8aalxDpoIMsXF6XyqS04j7Wpt4hjW6P7TAnacU0jhxPpMBqUj7t3ookXvQH'
  //     currentTime: string; // '2023-03-16T12:13:38Z'
  //     expiredTime: string; // '2023-03-16T11:43:38Z'
  // }>('/ccs/api/upload/sts_token');
  // if (checkSuccessResponse(res.code)) {
  //     return res.data;
  // }
  const rsp = await get2<{
    token: {
      sessionToken: string;
      accessKeyId: string;
      secretAccessKey: string;
      currentTime: string;
      expiredTime: string;
    };
  }>(
    `${bffUploadPath}/getUploadVideoAuth`,
    { region },
    {
      headers: {
        'upload-key': secretKey || ''
      }
    }
  );
  if (checkSuccessResponse(rsp.code)) {
    const { token } = rsp.data || {};
    return token;
  }
  message.error('Upload failed');
}

async function fetchVideoUploadAuth() {
  const res = await get2<{
    region: string;
    spaceName: string; // 'ad_site'
    uploadHost: string; // 'https://vod-ap-singapore-1.bytevcloudapi.com'
    // eslint-disable-next-line max-len
    sessionToken: string; // 'STS2eyJMVEFjY2Vzc0tleUlEIjoiQUtBUE1qYzBZell4TldaaU5tRmxOREl5T1RrNE9XVmpNek0zTVdNNVpqQTRaRFEiLCJBY2Nlc3NLZXlJRCI6IkFLVFBaakV3TkRobFlUUmpZVGc0TkRnMk1UazFaalZpTjJFMlpEYzFNMk16WWprIiwiU2lnbmVkU2VjcmV0QWNjZXNzS2V5IjoiMHQyRWNsT3R1bmFLTG55TGxhU1VjWkpXTDVWRUUrZWd6eEMyVWZHQjkvK3o1WmY5emdRMGgxWERjWDI2K1YyMWRwZ1pDMUpJak9pOUtHRmZpaFFQcHloc2t3U3lZRHZ2Z2FCVVU0ZHhUc2s9IiwiRXhwaXJlZFRpbWUiOjE2Nzg5Njg4MTgsIlBvbGljeVN0cmluZyI6IntcIlN0YXRlbWVudFwiOlt7XCJFZmZlY3RcIjpcIkFsbG93XCIsXCJBY3Rpb25cIjpbXCJ2b2Q6KlwiLFwiSW1hZ2VYOipcIl0sXCJSZXNvdXJjZVwiOltcIipcIl19LHtcIkVmZmVjdFwiOlwiQWxsb3dcIixcIkFjdGlvblwiOltcIlBTTVwiXSxcIlJlc291cmNlXCI6W1wiYWQudHRjeC5jZmNcIl19XX0iLCJTaWduYXR1cmUiOiI0MGNiNjk2YzY0NWRmMjIwZjgyZjFmZjE1YjU0YzVhZjg4OGE1NDc0OTlkZDQ5MGM0YWQ5NjMzZTU0MjFhMTZjIn0'
    accessKeyId: string; // 'AKTPZjEwNDhlYTRjYTg4NDg2MTk1ZjViN2E2ZDc1M2MzYjk'
    secretAccessKey: string; // 'MH6Bb8aalxDpoIMsXF6XyqS04j7Wpt4hjW6P7TAnacU0jhxPpMBqUj7t3ookXvQH'
    currentTime: string; // '2023-03-16T12:13:38Z'
    expiredTime: string; // '2023-03-16T11:43:38Z'
  }>('/ccs/api/upload/sts_token');
  if (checkSuccessResponse(res.code)) {
    return res.data;
  }
  message.error('Upload failed');
}

async function reportErrorMetrics(params: {
  region: string;
  type: string;
  appName?: string;
  appId?: number;
}) {
  const {
    data: { countryCode = 'none' }
  } = await getUserLocation();
  await post2(`${bffUploadPath}/reportErrorMetrics`, {
    ...params,
    appName: params?.appName || 'none',
    appId: params?.appId || -1,
    userCountry: countryCode
  });
}

async function reportSuccessMetrics(params: {
  region: string;
  type: string;
  appName?: string;
  appId?: number;
  size: number;
  duration: number;
}) {
  const {
    data: { countryCode = 'none' }
  } = await getUserLocation();
  await post2(`${bffUploadPath}/reportSuccessMetrics`, {
    ...params,
    appName: params?.appName || 'none',
    appId: params?.appId || -1,
    userCountry: countryCode
  });
}

export async function publishUploadVideo(vid: string) {
  const rsp = await post2('/ccs/api/upload/publish_video', { vid });
  if (!checkSuccessResponse(rsp.code)) {
    throw new Error('video publish failed');
  }
}

type ValueType<T> = T extends Promise<infer U> ? U : T;

let bytedUploader: BytedUploader;
let videoAndImageUploadInitParams: ValueType<ReturnType<typeof getUploaderInitParams>>;

// create a new uploader instance provided by video arch team
const createVideoArchUploader = async (fileName: string, config?: { region?: string; userId?: string }) => {
  if (!videoAndImageUploadInitParams) {
    videoAndImageUploadInitParams = await getUploaderInitParams(config?.region);
  }
  if (videoAndImageUploadInitParams && !bytedUploader) {
    const { region, spaceName, uploadHost, imageServiceId } = videoAndImageUploadInitParams;
    const uploadConfig: BytedUploaderConfig = {
      userId: config?.userId || defaultUploadUserId,
      appId: defaultUploadAppId,
      region: NewUploaderRegionsMap[region] || region,
      imageHost: isLocal ? 'https://vas-boei18n.remove.this' : NewUploaderImageHostMap[region || 'sg'],
      videoHost: uploadHost,
      imageConfig: {
        serviceId: imageServiceId
      },
      videoConfig: {
        spaceName,
        processAction: [
          {
            name: 'GetMeta'
          },
          {
            name: 'Snapshot',
            input: {
              SnapshotTime: 1
            }
          },
          {
            name: 'AddOptionInfo',
            input: {
              Title: fileName
            }
          }
        ]
      }
    };
    const finalConfig = {
      ...uploadConfig
      // enableDiskBreakpoint: false
    };

    bytedUploader = new BytedUploader(finalConfig);
  }

  return [bytedUploader, videoAndImageUploadInitParams] as [
    BytedUploader,
    typeof videoAndImageUploadInitParams
  ];
};

function getRegion(region?: string) {
  if (region) {
    return region;
  }
  if (isLocal) {
    return 'boei18n';
  }
  return window.location.host.includes('va') ? 'va' : 'sg';
}

export const uploadMedia = async (options: {
  region?: string;
  file: File;
  updateProgress?: (progress: number) => void;
  // secretKey for BFF api call, waiting for sunset after switch to fetching upload token through backend
  secretKey?: string;
}) => {
  const { file, updateProgress, secretKey } = options;
  const region = getRegion(options.region);
  const [mediaUploader, uploaderInitParams] = await createVideoArchUploader(file.name, { region });
  const isImage = file.type?.indexOf(UploadTypeEnum.IMAGE) !== -1;

  let fileKeyRef: string;
  const { name: fileName, size = 0 } = file;

  async function uploadMediaPromise() {
    let startUploadTime: number;

    return new Promise((resolve: (res: SuccessDataSchema) => void, reject) => {
      mediaUploader.on('error', (error) => {
        if (error.key && error.key !== fileKeyRef) {
          return;
        }
        reportErrorMetrics({
          region: uploaderInitParams?.region || '',
          type: isImage ? 'image' : 'video'
        });
        reject(error);
      });
      mediaUploader.on('progress', (res: any) => {
        if (res.key && res.key !== fileKeyRef) {
          return;
        }
        if (typeof updateProgress === 'function') {
          updateProgress((res?.percent || 0) / 100);
        }
      });
      mediaUploader.on('complete', async (result: any) => {
        if (result.key && result.key !== fileKeyRef) {
          return;
        }
        const duration = Date.now() - startUploadTime;
        reportSuccessMetrics({
          region: uploaderInitParams?.region || '',
          size,
          duration,
          type: isImage ? 'image' : 'video'
        });
        console.log(
          'upload complete: endTime:%c. startTime:%c, fileName:%C',
          Date.now(),
          startUploadTime,
          file.name
        );
        if (isImage) {
          const finalUrl = isLocal
            ? generateBoeImageUrl(result.uploadResult.ImageUri)
            : generateImageUrl({
                ...result.uploadResult,
                region
              });
          resolve({
            type: UploadTypeEnum.IMAGE,
            cover: finalUrl,
            name: fileName,
            source: finalUrl,
            cdnPath: finalUrl,
            modifyTime: Math.floor(new Date().getTime() / 1000),
            imageInfo: result?.uploadResult
          } as SuccessDataSchema);
        } else {
          const vid = result?.uploadResult.Vid as string;
          await publishUploadVideo(vid);
          const videoDuration = result.uploadResult?.VideoMeta?.Duration || 0;
          const uploadResult = (result.uploadResult || {}) as {
            PosterUri: string;
            Vid: string;
            VideoMeta: {
              Bitrate?: number;
              Duration?: number;
              FileType?: string;
              Format?: string;
              Width?: number;
              Height?: number;
              Md5?: string;
              OriginWidth?: number;
              OriginHeight?: number;
              Size?: number;
              Uri?: string;
            };
          };
          const videoPlayInfo = await getVideoInfoByTosUri(
            uploadResult?.VideoMeta?.Uri || '',
            uploadResult?.Vid
          );
          const videoPlayUrl = videoPlayInfo?.mainUrl;
          resolve({
            source: vid,
            name: fileName,
            type: UploadTypeEnum.VIDEO,
            cover: getTosFileUrl(uploadResult.PosterUri),
            videoInfoList: [
              {
                ...transformObjToCase({ ...uploadResult.VideoMeta }, 'camel'),
                mainUrl: videoPlayUrl,
                mainPlayUrl: videoPlayUrl
              }
            ],
            duration: Math.round(videoDuration),
            modifyTime: Math.floor(new Date().getTime() / 1000),
            videoInfo: result?.uploadResult,
            videoPlayUrl,
            videoOriginalUrl: videoPlayUrl
          } as SuccessDataSchema);
        }
      });
      if (isImage) {
        fetchImageUploadAuth(region, secretKey)
          .then((auth) => {
            if (!auth) {
              return reject({ message: 'Upload media failed --> not auth' }); // eslint-disable-line
            }
            const fileKey = mediaUploader.addFile({
              file,
              type: UploadTypeEnum.IMAGE,
              stsToken: {
                CurrentTime: auth.currentTime,
                ExpiredTime: auth.expiredTime,
                SessionToken: auth.sessionToken,
                AccessKeyID: auth.accessKeyId,
                SecretAccessKey: auth.secretAccessKey
              }
            } as any);
            fileKeyRef = fileKey;
            mediaUploader.start(fileKey);
            startUploadTime = Date.now();
          })
          .catch((err) => reject({ message: 'Upload media failed --> ' + err?.message })); // eslint-disable-line
      } else {
        fetchVideoUploadAuth()
          .then((auth) => {
            if (!auth) {
              return reject({ message: 'Upload media failed --> not auth' }); // eslint-disable-line
            }
            const fileKey = mediaUploader.addFile({
              file,
              type: UploadTypeEnum.VIDEO,
              stsToken: {
                CurrentTime: auth.currentTime,
                ExpiredTime: auth.expiredTime,
                SessionToken: auth.sessionToken,
                AccessKeyID: auth.accessKeyId,
                SecretAccessKey: auth.secretAccessKey
              }
            } as any);
            fileKeyRef = fileKey;

            mediaUploader.start(fileKey);
            startUploadTime = Date.now();
          })
          .catch((err) => reject({ message: 'Upload media failed --> ' + err?.message })); // eslint-disable-line
      }
    });
  }

  return {
    promise: uploadMediaPromise(),
    abortFunc: () => {
      if (mediaUploader && fileKeyRef) {
        mediaUploader.cancel(fileKeyRef);
      }
    }
  };
};

export const uploadSlice = (options: {
  file: File;
  updateProgress?: (progress: number) => void;
  secretKey: string;
}) => {
  const { file, updateProgress, secretKey } = options;
  const progressHandler = (args?: { percent?: number }) => {
    if (typeof updateProgress === 'function') {
      updateProgress((args?.percent ?? 0) / 100);
    }
  };
  const tosUploader = new SliceUploader();
  const promise = new Promise((resolve: (SuccessDataSchema) => void, reject) => {
    const successHandler = (args?: {
      name: string;
      response: {
        code: number;
        data: { key: string };
        msg: string;
      };
    }) => {
      const key = args?.response?.data?.key;
      if (!key) {
        reject(new Error('Upload slice failed --> not key'));
        return;
      }
      // eslint-disable-next-line no-console
      resolve({
        name: args?.name ?? '',
        type: getExtensionFromName(args?.name),
        source: key,
        cdnPath: getCDNPath(key),
        modifyTime: Math.floor(new Date().getTime() / 1000)
      } as SuccessDataSchema);
    };
    const errorHandler = (e) => {
      reject(e);
    };
    const sliceConfig = {
      onProgress: progressHandler,
      onSuccess: successHandler,
      onError: errorHandler,
      // 6MB to make progress bar smoother
      chunkSize: 6,
      concurrencyNum: 6,
      retryTimes: 1,
      fetchConfig: {
        xsrfCookieName: 'csrftoken',
        xsrfHeaderName: 'X-csrftoken',
        headers: {
          'upload-key': secretKey,
          'x-ccs-csrf-token': Cookie.get('ccs-csrf-token') || ''
        }
      },
      chunkUploadHandler: (result: any) => {
        const response = result.data;
        if (!response) {
          return Promise.reject(new Error('Upload slice failed --> Network error'));
        }
        if (!checkSuccessResponse(response.code)) {
          return Promise.reject(
            new Error(`Upload slice failed --> ${response.msg || `Error code:${response.code}`}`)
          );
        }
        return response.data.part_number;
      },
      initApi: `${bffUploadPath}/sliceUpload/init`,
      uploadApi: `${bffUploadPath}/sliceUpload/part`,
      completeApi: `${bffUploadPath}/sliceUpload/complete`
    };
    tosUploader.init(file, sliceConfig);
    tosUploader.start();
  });

  return {
    promise,
    abortFunc: () => {
      if (tosUploader) {
        tosUploader.abort();
      }
    }
  };
};

export const uploadSmallFile = (
  file: File,
  secretKey: string,
  updateProgress?: (progress: number) => void
) => {
  const formData = new FormData();
  formData.append('tcex-file', file);
  let smallTimer;
  if (typeof updateProgress === 'function') {
    smallTimer = uploaderTimerFunc(updateProgress);
  }
  const cancelTokenSource = Axios.CancelToken.source();

  async function uploadSmallFilePromise() {
    console.log('x-ccs-csrf-token', Cookie.get('ccs-csrf-token'));
    const res = await post2<{
      uri: string;
      name: string;
      type: string;
    }>(`${bffUploadPath}/smallFileUpload`, formData, {
      cancelToken: cancelTokenSource.token,
      headers: {
        'upload-key': secretKey,
        'x-ccs-csrf-token': Cookie.get('ccs-csrf-token') || '',
        'content-type': 'application/x-www-form-urlencoded;charset=utf-8'
      },
      timeout: 30000
    });
    clearInterval(smallTimer);
    if (!checkSuccessResponse(res.code) || !res.data) {
      throw new Error('Upload small file failed ---> ' + res.msg); // eslint-disable-line
    }
    const { uri, name, type } = res.data;
    return {
      source: uri,
      name,
      type,
      cdnPath: getCDNPath(uri),
      modifyTime: Math.floor(new Date().getTime() / 1000)
    } as SuccessDataSchema;
  }

  const promise = uploadSmallFilePromise();
  return {
    promise,
    abortFunc: () => {
      cancelTokenSource.cancel();
    }
  };
};

export const multiPurposeUpload = async (
  file: File,
  config: UploadConfigSchema,
  secretKey: string
): Promise<{ promise: any; abortFunc: AbortFunction }> => {
  const { region, setProgress: updateProgress, ...rest } = config || {};

  let promise: Promise<SuccessDataSchema>;
  let abortFunc: () => void;
  const { type, size } = file;
  if (typeJudge(type) === 'video' || typeJudge(type) === 'image') {
    ({ promise, abortFunc } = await uploadMedia({
      file,
      updateProgress,
      region,
      secretKey,
      ...rest
    }));
  } else if (size < 6 * 1024 * 1024) {
    ({ promise, abortFunc } = uploadSmallFile(file, secretKey, updateProgress));
  } else {
    ({ promise, abortFunc } = uploadSlice({
      file,
      updateProgress,
      secretKey
    }));
  }
  return {
    promise,
    abortFunc
  };
};

export type AbortFunction = () => void;

function useAllFileUpload(config: UploadConfigSchema): {
  upload: (file: File, fileId?: string) => Promise<AbortFunction | undefined>; // upload execution
} {
  if (config?.isInternal) {
    bffUploadPath = '/ccs_bff/upload/internal';
  }
  const upload = useCallback(
    async (file: File, fileId?: string) => {
      const { data: secretKey, code } = await fetchSecretKey((err) => config.onFail!(err, fileId));
      // upload
      if (!checkSuccessResponse(code)) {
        return;
      } else {
        config.setProgress && config.setProgress(0);
        var fileName = file.name;
        var fileType = file.type;

        // Check file name && extension
        if (fileName.endsWith('.xml') || fileType === 'application/xml' || fileType === 'text/xml') {
          config?.onFail?.(new Error('Invalid filetype'), fileId);
          return;
        }

        if (typeJudge(file.type) === 'zip' && config?.isCSF) {
          const { data: secretKeyLottieJson, code } = await fetchSecretKey((err) =>
            config.onFail!(err, fileId)
          );
          if (!checkSuccessResponse(code)) {
            return;
          } else {
            let zipResult;
            try {
              zipResult = await parseZipFile(file);
            } catch (err) {
              if (config.onFail) {
                config.onFail(new Error('Not a valid zip file'), fileId);
                return;
              }
            }
            const searchResult = checkLottieZip(zipResult);
            if (!searchResult && config.onFail) {
              config.onFail(new Error('Not a valid lottie zip file'), fileId);
              return;
            } else if (searchResult && config.onSuccess && config.onFail) {
              return resolveLottie(
                file,
                zipResult,
                searchResult,
                config,
                secretKeyLottieJson,
                secretKey,
                fileId
              );
            } else {
              // if somehow, no config was passed in
              alert('Zip file upload failed');
              throw new Error('Zip file upload failed');
            }
          }
        } else {
          const { abortFunc, promise } = await multiPurposeUpload(file, config, secretKey);
          promise
            .then((res: SuccessDataSchema) => {
              if (config.onSuccess) {
                config.onSuccess(res, undefined, undefined, fileId);
              }
            })
            .catch((err: Error) => {
              if (config.onFail) {
                config.onFail(err, fileId);
              }
            });
          return abortFunc;
        }
      }
    },
    [config.onSuccess, config.onFail]
  );

  return {
    upload
  };
}

export default useAllFileUpload;
